import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    accountNumber: state.components.contactUsForm.fieldValues["accountNumber"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountNumberStyles {
    accountNumberTextField?: TextFieldPresentationProps;
}

export const accountNumberStyles: AccountNumberStyles = {};

const styles = accountNumberStyles;

const AccountNumber: React.FC<Props> = ({ fields, accountNumber, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [accountNumberErrorMessage, setAccountNumberErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.accountNumber = validateAccountNumber;
        if (typeof accountNumber !== "undefined") {
            validateAccountNumber();
        }
    }, [accountNumber]);

    React.useEffect(() => {
        contactFormContext.validators.accountNumber = validateAccountNumber;
    }, [isRequired]);

    const validateAccountNumber = () => {
        const errorMessage = isRequired && !accountNumber ? (siteMessage("Field_Required", label) as string) : "";
        setAccountNumberErrorMessage(errorMessage);
        return !errorMessage;
    };

    const accountNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "accountNumber", value: event.target.value });
    };

    return (
        <TextField
            {...styles.accountNumberTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={accountNumber || ""}
            error={accountNumberErrorMessage}
            onChange={accountNumberChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountNumber),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Account Number",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Enter account number",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
